import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { PLATFORM, NAV } from "@/config/platform";

export function PlatformLogo({ testId = "platform-logo", light = false }) {
  return (
    <Link to="/" data-testid={testId} aria-label={`${PLATFORM.name} — accueil`} className="group inline-flex items-center gap-2">
      <img src={PLATFORM.logoIcon} alt="" className="h-10 w-auto transition-transform duration-300 group-hover:scale-105" />
      <img src={PLATFORM.logoWordmark} alt={PLATFORM.name} className={`h-6 w-auto sm:h-7 ${light ? "brightness-0 invert" : ""}`} />
    </Link>
  );
}

const navCls = ({ isActive }) =>
  `relative py-1 text-[15px] transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:bg-gold after:transition-transform after:duration-300 ${
    isActive ? "text-bx after:scale-x-100" : "text-ink/75 hover:text-bx after:scale-x-0 hover:after:scale-x-100"
  }`;

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <header className="sticky top-0 z-40 border-b border-line/70 bg-ivory/85 backdrop-blur-md print:hidden" data-testid="platform-header">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
        <PlatformLogo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigation principale">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} className={navCls} data-testid={`pnav-${n.to.slice(1)}`}>{n.label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/mon-espace" data-testid="pnav-my-space"
            className="hidden min-h-[42px] items-center rounded-full bg-bx px-5 text-sm font-medium text-white transition-colors hover:bg-bx-dark sm:inline-flex">
            Mon espace
          </Link>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Menu" data-testid="pnav-menu-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-bx lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="animate-fade-up border-t border-line bg-ivory px-5 pb-6 pt-2 lg:hidden" data-testid="pnav-mobile">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`pnav-mobile-${n.to.slice(1)}`}
              className={({ isActive }) => `flex items-center justify-between border-b border-line py-4 text-lg ${isActive ? "text-bx" : "text-ink"}`}>
              {n.label}<ArrowRight className="h-4 w-4 text-gold" />
            </NavLink>
          ))}
          <Link to="/mon-espace" data-testid="pnav-mobile-my-space" className="mt-5 flex min-h-[50px] items-center justify-center rounded-full bg-bx text-white">Mon espace</Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#161215] text-white/75 print:hidden" data-testid="platform-footer">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <img src={PLATFORM.logoIcon} alt="" className="h-12 w-auto" />
          <p className="mt-4 font-display text-2xl text-white">{PLATFORM.name}</p>
          <p className="mt-2 max-w-xs text-sm">{PLATFORM.tagline}.</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">La plateforme</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
            <li><Link to="/mon-espace" className="hover:text-white">Mon espace</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Informations</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link to="/mentions-legales" className="hover:text-white" data-testid="footer-legal">Mentions légales</Link></li>
            <li><Link to="/cgv" className="hover:text-white" data-testid="footer-cgv">CGV</Link></li>
            <li><Link to="/confidentialite" className="hover:text-white" data-testid="footer-privacy">Confidentialité</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/45">© {new Date().getFullYear()} {PLATFORM.name} · {PLATFORM.domain}</div>
    </footer>
  );
}

export default function PlatformLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-ivory" data-testid="platform-layout">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
